import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store/index';
import ApiEvents from '@/api/ApiEvents';
import {
  FetchEventsRequest,
} from '@/model/Events';
import {
  YearlyEventBulkResponseForHome,
  YearlyEventIncludingChildrenResponseForHome,
} from '@API/src/component/home/yearlyEvent/types';

import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

export interface EventsState {
  events: YearlyEventIncludingChildrenResponseForHome[];
  total: number;
}

@Module({ dynamic: true, store, name: 'events', namespaced: true })
class EventsModule extends VuexModule implements EventsState {

  public events: YearlyEventIncludingChildrenResponseForHome[] = [];
  public total: number = 0;

  public get eventsIncludedDate(): YearlyEventIncludingChildrenResponseForHome[] {
    // 1. 日付ごとにイベントを集約（元のデータを壊さないようにディープコピー気味に処理）
    const groupedEvents = this.events.reduce((acc: YearlyEventIncludingChildrenResponseForHome[], current) => {
      const existingDateIndex = acc.findIndex((a) => a.date === current.date);

      if (existingDateIndex === -1) {
        // 参照を切るために新しいオブジェクトとして追加
        acc.push({
          ...current,
          children: [...current.children]
        });
      } else {
        // 既に同じ日付がある場合は、イベント名を結合し、子供リストをマージ
        acc[existingDateIndex].eventName = `${acc[existingDateIndex].eventName}\n${current.eventName}`;
        acc[existingDateIndex].children = [
          ...acc[existingDateIndex].children,
          ...current.children
        ];
        // 子供の重複（同じ子が別イベントに紐付いている場合）を除去したい場合はここでfilterを入れる
        const ids = acc[existingDateIndex].children.map(c => c.childId);
        acc[existingDateIndex].children = acc[existingDateIndex].children.filter((item, index) => ids.indexOf(item.childId) === index);
      }
      return acc;
    }, []);

    // 2. 卒園・退園済みを省き、該当園児のいないイベントを削る
    const result = groupedEvents.map((event) => {
      const filteredChildren = event.children.filter((c) => {
        // 卒園日or退園日の早い方を選出
        let leaveDate: string | null = null;
        if (c.graduationDate && c.quitDate) {
          leaveDate = dayjs(c.graduationDate).isAfter(c.quitDate) ? c.quitDate : c.graduationDate;
        } else {
          leaveDate = c.graduationDate || c.quitDate || null;
        }

        // 卒退園日当日以降（isBeforeではない）のイベントから除外
        if (leaveDate) {
          return dayjs(event.date).isBefore(dayjs(leaveDate));
        }
        return true;
      });

      return { ...event, children: filteredChildren };
    }).filter((event) => event.children.length > 0); // 園児が0人のイベントを除外

    // 3. 昇順に並び替え
    return result.sort((a, b) => {
      const aTime = dayjs(a.date);
      const bTime = dayjs(b.date);
      return aTime.isBefore(bTime) ? -1 : 1;
    });
  }

  @Action({ rawError: true })
  public async fetchEvents(request: FetchEventsRequest) {
    try {
      const response = await ApiEvents.fetchEvents(request);
      this.setEvents(response.yearlyEvents);
    } catch (error) {
      throw error;
    }
  }

  @Mutation
  public setEvents(events: YearlyEventIncludingChildrenResponseForHome[]) {
    this.events = events;
  }
}

export const eventsModule = getModule(EventsModule);
